import { useCallback, useEffect, useRef, useState } from 'react';
import { MEETING_END_WARNING_MS } from '../utils/meetingDuration';

const WARNING_DISPLAY_MS = 6000;

/**
 * Warns once near scheduledEndTime (banner auto-hides), then fires onEnd at end.
 */
export function useMeetingEndTimer({ scheduledEndTime, enabled = true, onEnd }) {
  const [warningVisible, setWarningVisible] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const endedRef = useRef(false);
  const warningShownRef = useRef(false);
  const hideTimerRef = useRef(null);
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  const endMs = scheduledEndTime ? new Date(scheduledEndTime).getTime() : NaN;

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current != null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const showWarningBriefly = useCallback(() => {
    if (warningShownRef.current) return;
    warningShownRef.current = true;
    setWarningVisible(true);
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      setWarningVisible(false);
      hideTimerRef.current = null;
    }, WARNING_DISPLAY_MS);
  }, [clearHideTimer]);

  const tick = useCallback(() => {
    if (!enabled || !Number.isFinite(endMs) || endedRef.current) return;

    const now = Date.now();
    const remaining = endMs - now;

    if (remaining <= 0) {
      endedRef.current = true;
      clearHideTimer();
      setWarningVisible(false);
      setSecondsLeft(0);
      onEndRef.current?.();
      return;
    }

    setSecondsLeft(Math.ceil(remaining / 1000));
    if (remaining <= MEETING_END_WARNING_MS) {
      showWarningBriefly();
    }
  }, [enabled, endMs, clearHideTimer, showWarningBriefly]);

  useEffect(() => {
    endedRef.current = false;
    warningShownRef.current = false;
    clearHideTimer();
    setWarningVisible(false);
    setSecondsLeft(null);
  }, [endMs, clearHideTimer]);

  useEffect(() => {
    if (!enabled || !Number.isFinite(endMs)) return undefined;

    tick();
    const id = window.setInterval(tick, 1000);
    return () => {
      window.clearInterval(id);
      clearHideTimer();
    };
  }, [enabled, endMs, tick, clearHideTimer]);

  return { warningVisible, secondsLeft };
}
